import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import apiClient from '../../Services/ApiClient';
import { P21CustomerMatch } from '../../Types/customerAccounts';
import { textFieldSx } from '../../Components/Form';

/**
 * Pick a P21 customer by number or name. Searches P21 as you type (2+ characters),
 * so staff never type customer ids blind.
 */
const P21CustomerPicker: React.FC<{
  value: P21CustomerMatch | null;
  onChange: (customer: P21CustomerMatch | null) => void;
  exclude?: string[];
  label?: string;
  disabled?: boolean;
}> = ({ value, onChange, exclude = [], label = 'P21 customer', disabled }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<P21CustomerMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const term = input.trim();
    if (term.length < 2) {
      setOptions([]);
      setError('');
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await apiClient(`/api/customer-accounts/p21-customers?search=${encodeURIComponent(term)}`);
        const body = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          setError(body.error || 'Could not search P21');
          setOptions([]);
        } else {
          setError('');
          setOptions(body.customers || []);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not search P21');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, customer) => onChange(customer)}
      inputValue={input}
      onInputChange={(_, text) => setInput(text)}
      options={options.filter((o) => !exclude.includes(o.customerId))}
      filterOptions={(x) => x}
      getOptionLabel={(o) => `${o.customerId} · ${o.customerName ?? 'Unnamed customer'}`}
      isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
      loading={loading}
      disabled={disabled}
      noOptionsText={input.trim().length < 2 ? 'Type a customer number or name' : 'No matching P21 customer'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={!!error}
          helperText={error || 'Search by P21 customer number or name'}
          sx={textFieldSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default P21CustomerPicker;
